import { useCallback, useRef, useState, type DragEvent } from "react";
import { Download, FileText, FolderOpen, Loader2, Trash2 } from "lucide-react";

import { RfqAttachDocumentModal, useRfqSkuDocumentAccessPresentation } from "@/components/mrp/RfqAttachDocumentModal";

import { HubAttachmentPreviewModal } from "@/components/HubAttachmentPreviewModal";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import type { MrpRfqAttachment } from "@/lib/rfqApi";

const RFQ_ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.txt,.zip,.step,.stp,.iges,.igs,.stl,.obj,.3mf,.gltf,.glb,.dwg,.dxf,.x_t,.x_b,.prt,.asm,.sldprt,.sldasm";

const RFQ_ATTACHMENT_LIST_SCROLL_CLASS_COMPACT = "max-h-[min(14rem,38vh)] overflow-y-auto overscroll-y-contain";
const RFQ_ATTACHMENT_LIST_SCROLL_CLASS = "max-h-[min(18rem,45vh)] overflow-y-auto overscroll-y-contain";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAttachmentCount(count: number): string {
  return count === 1 ? "1 file" : `${count} files`;
}

export function RfqAttachmentsPanel({
  title,
  hoverHint,
  attachments,
  disabled = false,
  uploadDisabled = false,
  uploadDisabledHint = "Save or create the RFQ before uploading files.",
  emptyListMessage = null,
  isUploading = false,
  compact = false,
  uploadStatus = null,
  uploadError = null,
  onUpload,
  onAttachFromDocument,
  onDownload,
  onDelete,
  onResolvePreviewUrl,
  sku = null,
  isAttachingFromDocument = false,
}: {
  title: string;
  hoverHint?: string;
  attachments: MrpRfqAttachment[];
  disabled?: boolean;
  uploadDisabled?: boolean;
  uploadDisabledHint?: string;
  emptyListMessage?: string | null;
  isUploading?: boolean;
  compact?: boolean;
  uploadStatus?: string | null;
  uploadError?: string | null;
  onUpload: (file: File) => void;
  onAttachFromDocument?: (documentRecordIds: string[]) => void | Promise<void>;
  onDownload: (attachment: MrpRfqAttachment) => void;
  onDelete: (attachment: MrpRfqAttachment) => void;
  onResolvePreviewUrl: (attachment: MrpRfqAttachment) => Promise<{ url: string; filename: string }>;
  sku?: string | null;
  isAttachingFromDocument?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<MrpRfqAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const skuDocumentAccess = useRfqSkuDocumentAccessPresentation(sku ?? "");

  const applyFile = useCallback(
    (file: File | null) => {
      if (file == null || disabled || uploadDisabled || isUploading) return;
      onUpload(file);
      if (fileInputRef.current != null) {
        fileInputRef.current.value = "";
      }
    },
    [disabled, isUploading, onUpload, uploadDisabled],
  );

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled || uploadDisabled || isUploading) return;
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    },
    [disabled, isUploading, uploadDisabled],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled || uploadDisabled || isUploading) return;
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    },
    [disabled, isUploading, uploadDisabled],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled || uploadDisabled || isUploading) return;
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const dropped = event.dataTransfer.files?.[0];
      if (dropped != null) {
        applyFile(dropped);
      }
    },
    [applyFile, disabled, isUploading, uploadDisabled],
  );

  const closePreview = useCallback(() => {
    setPreviewAttachment(null);
    setPreviewUrl(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }, []);

  const openPreview = useCallback(
    async (attachment: MrpRfqAttachment) => {
      setPreviewAttachment(attachment);
      setPreviewUrl(null);
      setPreviewError(null);
      setPreviewLoading(true);
      try {
        const result = await onResolvePreviewUrl(attachment);
        setPreviewUrl(result.url);
      } catch (error) {
        setPreviewError(error instanceof Error ? error.message : "Could not load preview.");
      } finally {
        setPreviewLoading(false);
      }
    },
    [onResolvePreviewUrl],
  );

  const body = (
    <div className="space-y-4">
      {!disabled ? (
        <div className="flex flex-wrap items-center gap-2">
          <div
            className={cn(
              "inline-flex min-h-9 items-center justify-center rounded-md border border-dashed px-3 text-sm transition-colors",
              compact ? "min-h-8 text-xs" : "",
              dragActive
                ? "border-brand-500 bg-brand-50"
                : uploadError != null
                  ? "border-red-400 bg-red-50 hover:border-red-500"
                  : "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50",
              (disabled || uploadDisabled || isUploading) && "pointer-events-none opacity-60",
            )}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (!uploadDisabled) {
                fileInputRef.current?.click();
              }
            }}
            title={uploadDisabled ? uploadDisabledHint : undefined}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={RFQ_ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(event) => applyFile(event.target.files?.[0] ?? null)}
            />
            {isUploading ? (
              <span className="inline-flex items-center gap-1.5 text-slate-700">
                <Loader2 className="h-4 w-4 animate-spin text-brand-600" aria-hidden />
                {uploadStatus ?? "Preparing attachment upload"}
              </span>
            ) : uploadError != null ? (
              <span className="text-red-800">{uploadError}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-slate-700">
                <FileText className="h-4 w-4 text-slate-500" aria-hidden />
                Upload file
              </span>
            )}
          </div>
          {onAttachFromDocument != null ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={isUploading || isAttachingFromDocument}
              isLoading={isAttachingFromDocument}
              onClick={() => setPickerOpen(true)}
            >
              <FolderOpen className="mr-1.5 h-4 w-4" aria-hidden />
              Add from documents
            </Button>
          ) : sku != null && sku.trim() !== "" ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>
              <FolderOpen className="mr-1.5 h-4 w-4" aria-hidden />
              {skuDocumentAccess.buttonLabel}
            </Button>
          ) : null}
          {uploadDisabled ? (
            <p className={cn("text-slate-500", compact ? "text-xs" : "text-sm")}>{uploadDisabledHint}</p>
          ) : null}
        </div>
      ) : null}

      {attachments.length === 0 ? (
        <p className={cn("text-slate-500", compact ? "text-xs" : "text-sm")}>
          {emptyListMessage ?? "No files yet."}
        </p>
      ) : (
        <div
          className={cn(
            "rounded-lg border border-slate-200",
            compact ? RFQ_ATTACHMENT_LIST_SCROLL_CLASS_COMPACT : RFQ_ATTACHMENT_LIST_SCROLL_CLASS,
          )}
        >
          <ul className="divide-y divide-slate-100">
            {attachments.map((attachment) => (
              <li
                key={attachment.id}
                className={cn(
                  "flex items-center justify-between gap-3 px-3 py-2",
                  compact ? "text-xs" : "text-sm",
                )}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 cursor-pointer rounded-md text-left hover:bg-slate-50"
                  onClick={() => void openPreview(attachment)}
                >
                  <p className="truncate font-medium text-slate-900 hover:text-brand-700">{attachment.filename}</p>
                  <p className="text-xs text-slate-500">
                    {formatFileSize(attachment.file_size_bytes)} · {attachment.uploaded_at.slice(0, 10)}
                  </p>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => onDownload(attachment)}
                    aria-label={`Download ${attachment.filename}`}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                  </Button>
                  {!disabled ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => onDelete(attachment)}
                      aria-label={`Remove ${attachment.filename}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" aria-hidden />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const documentPicker =
    onAttachFromDocument != null || (sku != null && sku.trim() !== "") ? (
      <RfqAttachDocumentModal
        open={pickerOpen}
        sku={sku}
        canAttach={!disabled && onAttachFromDocument != null}
        viewerHasAccess={skuDocumentAccess.hasAccess}
        isSubmitting={isAttachingFromDocument}
        onClose={() => setPickerOpen(false)}
        onAttach={async (documentRecordIds) => {
          await onAttachFromDocument?.(documentRecordIds);
          setPickerOpen(false);
        }}
      />
    ) : null;

  if (compact) {
    return (
      <div className="rounded-md border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-3 py-2">
          <p className="text-xs font-semibold text-slate-800">
            <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              {title}
              <span className="font-normal tabular-nums text-slate-500">
                ({formatAttachmentCount(attachments.length)})
              </span>
              {hoverHint != null ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
            </span>
          </p>
        </div>
        <div className="p-3">{body}</div>
        {previewAttachment != null ? (
          <HubAttachmentPreviewModal
            filename={previewAttachment.filename}
            contentType={previewAttachment.content_type}
            previewUrl={previewUrl}
            isLoading={previewLoading}
            error={previewError}
            onClose={closePreview}
            onDownload={() => onDownload(previewAttachment)}
          />
        ) : null}
        {documentPicker}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            {title}
            <span className="text-base font-normal tabular-nums text-slate-500">
              ({formatAttachmentCount(attachments.length)})
            </span>
            {hoverHint != null ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
          </span>
        </CardTitle>
      </CardHeader>
      <CardBody>{body}</CardBody>
      {previewAttachment != null ? (
        <HubAttachmentPreviewModal
          filename={previewAttachment.filename}
          contentType={previewAttachment.content_type}
          previewUrl={previewUrl}
          isLoading={previewLoading}
          error={previewError}
          onClose={closePreview}
          onDownload={() => onDownload(previewAttachment)}
        />
      ) : null}
      {documentPicker}
    </Card>
  );
}
